import { Button, Group, Modal, Pagination, Radio, Space, Table, Text, TextInput, UnstyledButton } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { AxiosCommonError, ICompanyData } from '../../../../../shared/types';
import { handleError } from '../../../../ui/err/handleError';
import { companyApis } from '../../../../../shared/apis/company/apis';
import { useSearchInput } from './hooks/useSearchInput';
import { IconReload } from '@tabler/icons-react';

interface IPropsCompanySearchModal {
  opened: boolean;
  close: () => void;
  companyType: 'from' | 'to';
  updateCompany: (type: 'from' | 'to', company: ICompanyData) => void;
}

export default function CompanySearchModal(props: IPropsCompanySearchModal) {
  const { opened, close, companyType, updateCompany } = props;
  const [page, setPage] = useState<number>(1);
  const [selectedCompany, setSelectedCompany] = useState<ICompanyData | null>(null);

  const { inputRef, searchTerm, onSearch, onClear } = useSearchInput();
  const size = 10;

  const payload = {
    page,
    size,
    search: {
      name: searchTerm,
    },
  };

  const { data: companyList } = useQuery(['getCompanies', page, searchTerm], () => companyApis.companyList(payload), {
    select: (data) => data.data,
    onError: (error: AxiosError<AxiosCommonError>) => {
      handleError(error);
    },
  });

  const onConfirmCompany = () => {
    if (selectedCompany) {
      updateCompany(companyType, selectedCompany);
      close();
    }
  };

  return (
    <Modal opened={opened} onClose={close} title="거래처 조회 검색" centered size="lg">
      <Text size="sm" c="dimmed">
        ※ 거래처가 조회되지 않는 경우, 거래처를 우선 등록 후 진행해주세요.
      </Text>
      <Group mt="md" mb="xs">
        <TextInput placeholder="회사명으로 검색해주세요." ref={inputRef} />
        <Button
          onClick={() => {
            setPage(1);
            onSearch();
          }}
        >
          검색
        </Button>
        <UnstyledButton
          onClick={onClear}
          style={{
            cursor: 'pointer',
          }}
        >
          <IconReload size={16} />
          <Text size="sm" ml={4}>
            초기화
          </Text>
        </UnstyledButton>
      </Group>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>-</Table.Th>
            <Table.Th>번호</Table.Th>
            <Table.Th>회사명</Table.Th>
            <Table.Th>대표자명</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {companyList?.data.map((company: ICompanyData) => (
            <Table.Tr key={company.id}>
              <Table.Td>
                <Radio
                  styles={{
                    body: {
                      justifyContent: 'center',
                    },
                  }}
                  checked={company.id === selectedCompany?.id}
                  onChange={() => setSelectedCompany(company)}
                />
              </Table.Td>
              <Table.Td>{company.id}</Table.Td>
              <Table.Td>{company.name}</Table.Td>
              <Table.Td>{company.repName}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <Group justify="center" mt="md">
        <Pagination total={Math.ceil(companyList?.total / size)} value={page} onChange={setPage} />
      </Group>
      <Space h="lg" />
      <Group justify="center">
        <Button variant="outline" onClick={close}>
          취소
        </Button>
        <Button color="secondary.5" onClick={onConfirmCompany}>
          확인
        </Button>
      </Group>
    </Modal>
  );
}
